import { Link } from "@tanstack/react-router";
import { useUnitSystem, formatWeight, formatLength } from "@/lib/units";

/**
 * Per-model interpretation blocks for the auto-generated curb-weight and
 * dimensions pages.
 *
 * These pages previously shared the same table + boilerplate, so a model with
 * only two data rows looked like every other one. Everything below is derived
 * from that model's own numbers (spread, class band, garage/parking clearance,
 * generation delta), so no two pages read alike and the page answers the
 * question a searcher actually has.
 */

interface DimensionLike {
  id: string;
  year: number;
  variant_label: string | null;
  length_in: number | null;
  width_in: number | null;
  height_in: number | null;
  wheelbase_in: number | null;
  curb_weight_lbs: number | null;
}

/** EPA Automotive Trends Report: average new US vehicle ≈ 4,329 lb. */
const US_AVERAGE_CURB_WEIGHT_LBS = 4329;
/** Typical single garage door opening and stall depth, in inches. */
const GARAGE_DOOR_WIDTH_IN = 96;
const GARAGE_DOOR_HEIGHT_IN = 84;
const GARAGE_DEPTH_IN = 240;
/** Standard US surface parking stall: 9 ft x 18 ft. */
const STALL_WIDTH_IN = 108;
const STALL_LENGTH_IN = 216;

const round = (n: number) => Math.round(n);
const pct = (a: number, b: number) => Math.round(((a - b) / b) * 100);

function weightBand(lbs: number): { label: string; note: string } {
  if (lbs < 3000)
    return {
      label: "Lightweight",
      note: "Under 3,000 lb, so it sits in the lightest band on sale today — usually the best case for braking distance, tire wear, and fuel use.",
    };
  if (lbs < 4000)
    return {
      label: "Light-midweight",
      note: "Between 3,000 and 4,000 lb — typical compact-car territory, comfortably below the current US new-vehicle average.",
    };
  if (lbs < 5000)
    return {
      label: "Midweight",
      note: "Between 4,000 and 5,000 lb, the band most crossovers and midsize SUVs land in. Tire load ratings and brake wear scale with it.",
    };
  if (lbs < 6000)
    return {
      label: "Heavy",
      note: "Between 5,000 and 6,000 lb. Expect stiffer load-rated tires and longer stopping distances than a midsize car.",
    };
  return {
    label: "Very heavy (6,000 lb+)",
    note: "Above 6,000 lb, which is the threshold used for heavier-duty tire load ratings, some parking-structure limits, and several US weight-based vehicle classifications.",
  };
}

/** Unique, numbers-driven read of a model's curb weight. */
export function CurbWeightContext({
  dimensions,
  fullName,
}: {
  dimensions: DimensionLike[];
  fullName: string;
}) {
  const [unit] = useUnitSystem();
  const rows = dimensions.filter((d) => d.curb_weight_lbs != null);
  if (rows.length === 0) return null;

  const weights = rows.map((d) => d.curb_weight_lbs as number);
  const lightestRow = rows.reduce((b, d) =>
    (d.curb_weight_lbs as number) < (b.curb_weight_lbs as number) ? d : b,
  );
  const heaviestRow = rows.reduce((b, d) =>
    (d.curb_weight_lbs as number) > (b.curb_weight_lbs as number) ? d : b,
  );
  const lo = round(Math.min(...weights));
  const hi = round(Math.max(...weights));
  const spread = hi - lo;
  const band = weightBand(hi);
  const vsAverage = pct(hi, US_AVERAGE_CURB_WEIGHT_LBS);
  // Five occupants at the 150 lb figure used for occupant-load estimates.
  const loadedFive = hi + 750;
  const lengthRow = rows.find((d) => d.length_in);
  const lbsPerInch = lengthRow?.length_in
    ? Math.round((lengthRow.curb_weight_lbs as number) / lengthRow.length_in)
    : null;

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 border-t border-border">
      <h2 className="text-xl font-bold text-foreground">
        What the {fullName}'s curb weight actually means
      </h2>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Weight class
          </div>
          <div className="mt-1 text-lg font-semibold text-foreground">{band.label}</div>
          <p className="mt-2 text-sm text-muted-foreground">{band.note}</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Vs. average new US vehicle
          </div>
          <div className="mt-1 text-lg font-semibold text-foreground">
            {vsAverage === 0
              ? "About the same"
              : `${Math.abs(vsAverage)}% ${vsAverage > 0 ? "heavier" : "lighter"}`}
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            The average new vehicle sold in the US weighs about{" "}
            {formatWeight(US_AVERAGE_CURB_WEIGHT_LBS, unit)} (EPA Automotive Trends
            Report). This one is {formatWeight(hi, unit)} at its heaviest recorded
            configuration.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            With five people aboard
          </div>
          <div className="mt-1 text-lg font-semibold text-foreground">
            ≈ {formatWeight(loadedFive, unit)}
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Curb weight excludes people and cargo. Five occupants at 150 lb each add
            roughly 750 lb before luggage — that total is what your tires, brakes, and
            payload rating have to carry.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-3 text-sm text-muted-foreground">
        {spread > 0 ? (
          <p>
            Across the recorded {lightestRow.year === heaviestRow.year ? "variants" : "model years"} the{" "}
            {fullName} varies by <strong className="text-foreground">{formatWeight(spread, unit)}</strong> —
            from {formatWeight(lo, unit)}{" "}
            {lightestRow.variant_label ? `(${lightestRow.year} ${lightestRow.variant_label})` : `(${lightestRow.year})`}{" "}
            up to {formatWeight(hi, unit)}{" "}
            {heaviestRow.variant_label ? `(${heaviestRow.year} ${heaviestRow.variant_label})` : `(${heaviestRow.year})`}
            . Body style, drivetrain, battery pack, and glass roof options are the usual
            reasons two examples of the same car weigh different amounts, so match the
            row above to the exact configuration you are shopping.
          </p>
        ) : (
          <p>
            Every recorded configuration of the {fullName} comes in at about{" "}
            {formatWeight(hi, unit)}, so drivetrain and body-style choices on this model
            do not move the number meaningfully. Options fitted after the fact — roof
            racks, tow hitches, spare-wheel packages — are what change it in practice.
          </p>
        )}
        {lbsPerInch && (
          <p>
            Relative to its footprint, the {fullName} carries about{" "}
            <strong className="text-foreground">{lbsPerInch} lb per inch</strong> of
            overall length. Higher figures usually indicate a battery pack, a
            body-on-frame platform, or heavy sound deadening rather than simply a
            larger vehicle.
          </p>
        )}
        <p>
          Why it matters: curb weight drives stopping distance, tire load index, brake
          and suspension wear, ferry and toll classification in some regions, and — on
          towing vehicles — how much of the manufacturer's combined rating is used up
          before you hitch anything.
        </p>
      </div>
    </section>
  );
}

function fitVerdict(value: number | null, limit: number) {
  if (value == null) return null;
  const clearance = limit - value;
  return { clearance, fits: clearance > 0 };
}

/** Unique, numbers-driven read of a model's exterior dimensions. */
export function DimensionsContext({
  dimensions,
  fullName,
  makeSlug,
  modelSlug,
}: {
  dimensions: DimensionLike[];
  fullName: string;
  makeSlug: string;
  modelSlug: string;
}) {
  const [unit] = useUnitSystem();
  if (dimensions.length === 0) return null;

  const byYear = [...dimensions].sort((a, b) => b.year - a.year);
  const newest = byYear[0];
  const oldest = byYear[byYear.length - 1];
  if (!newest || !oldest) return null;
  const maxLength = Math.max(...dimensions.map((d) => d.length_in ?? 0)) || null;
  const maxWidth = Math.max(...dimensions.map((d) => d.width_in ?? 0)) || null;
  const maxHeight = Math.max(...dimensions.map((d) => d.height_in ?? 0)) || null;

  const doorWidth = fitVerdict(maxWidth, GARAGE_DOOR_WIDTH_IN);
  const doorHeight = fitVerdict(maxHeight, GARAGE_DOOR_HEIGHT_IN);
  const depth = fitVerdict(maxLength, GARAGE_DEPTH_IN);
  const stallWidth = fitVerdict(maxWidth, STALL_WIDTH_IN);
  const stallLength = fitVerdict(maxLength, STALL_LENGTH_IN);

  const lengthDelta =
    newest.length_in && oldest.length_in && newest.year !== oldest.year
      ? Math.round(newest.length_in - oldest.length_in)
      : null;
  const wheelbaseShare =
    newest.wheelbase_in && newest.length_in
      ? Math.round((newest.wheelbase_in / newest.length_in) * 100)
      : null;

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 border-t border-border">
      <h2 className="text-xl font-bold text-foreground">
        Will the {fullName} fit your garage and parking space?
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Measured against a standard single garage door (8 ft wide, 7 ft tall, 20 ft
        deep stall) and a standard US surface parking space (9 ft x 18 ft). Measure
        your own opening before buying — older garages are frequently narrower.
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Garage door width
          </div>
          {doorWidth ? (
            <>
              <div
                className={`mt-1 text-lg font-semibold ${doorWidth.fits ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}
              >
                {doorWidth.fits ? "Fits" : "Too wide"}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {formatLength(maxWidth, unit)} wide (body only, mirrors folded) —{" "}
                {doorWidth.fits
                  ? `about ${formatLength(doorWidth.clearance, unit)} of total clearance, so roughly ${formatLength(Math.round(doorWidth.clearance / 2), unit)} per side.`
                  : `it exceeds a standard 8 ft opening by ${formatLength(Math.abs(doorWidth.clearance), unit)}.`}
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">Width not recorded.</p>
          )}
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Garage door height
          </div>
          {doorHeight ? (
            <>
              <div
                className={`mt-1 text-lg font-semibold ${doorHeight.fits ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}
              >
                {doorHeight.fits ? "Clears" : "Too tall"}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {formatLength(maxHeight, unit)} tall —{" "}
                {doorHeight.fits
                  ? `${formatLength(doorHeight.clearance, unit)} of headroom before roof rails, antennas, or a roof box are added.`
                  : `over a standard 7 ft opening by ${formatLength(Math.abs(doorHeight.clearance), unit)}.`}
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">Height not recorded.</p>
          )}
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Parking space
          </div>
          {stallLength && stallWidth ? (
            <>
              <div
                className={`mt-1 text-lg font-semibold ${stallLength.fits && stallWidth.fits ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}
              >
                {stallLength.fits && stallWidth.fits ? "Fits a standard stall" : "Tight fit"}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {formatLength(maxLength, unit)} long leaves{" "}
                {formatLength(Math.abs(stallLength.clearance), unit)}{" "}
                {stallLength.fits ? "to spare" : "hanging over"} in an 18 ft space, and{" "}
                {formatLength(Math.abs(stallWidth.clearance), unit)}{" "}
                {stallWidth.fits ? "of side clearance" : "over the stall width"} in a 9 ft
                one.
                {depth && !depth.fits
                  ? " It also exceeds a 20 ft garage stall, so a rear-entry door would be blocked."
                  : ""}
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">Length not recorded.</p>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-3 text-sm text-muted-foreground">
        {lengthDelta !== null && lengthDelta !== 0 && (
          <p>
            Size drift: the {newest.year} {fullName} is{" "}
            <strong className="text-foreground">
              {formatLength(Math.abs(lengthDelta), unit)} {lengthDelta > 0 ? "longer" : "shorter"}
            </strong>{" "}
            than the {oldest.year} car in our records. If you are replacing an older
            example with a newer one, check the garage clearance again rather than
            assuming the outgoing car's footprint carries over.
          </p>
        )}
        {wheelbaseShare && (
          <p>
            Its wheelbase ({formatLength(newest.wheelbase_in, unit)}) accounts for about{" "}
            <strong className="text-foreground">{wheelbaseShare}%</strong> of overall
            length. A higher share generally means shorter overhangs — better approach
            and departure clearance on driveways and ramps, and more cabin space for the
            same parking footprint.
          </p>
        )}
        <p>
          Want to see it against another vehicle at the same scale? Open the{" "}
          <Link
            to="/car-size-comparison"
            search={{ a: undefined, b: undefined }}
            className="text-primary underline"
          >
            car size comparison tool
          </Link>{" "}
          or check the{" "}
          <Link
            to="/$make/$model/$dataType"
            params={{ make: makeSlug, model: modelSlug, dataType: "curb-weight" }}
            className="text-primary underline"
          >
            {fullName} curb weight
          </Link>{" "}
          to pair footprint with mass.
        </p>
      </div>
    </section>
  );
}
